import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Flex, Text, toast } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { DocumentUploadInviteStatus } from "../../../graphql/supergraphEnums";
import useGuestDocumentProcessor from "../../../hooks/documents/useGuestDocumentProcessor";
import useGuestDocumentUpload from "../../../hooks/documents/useGuestDocumentUpload";
import { formatDate } from "../../../utils/date_formatter";
import { getErrorMessage } from "../../../utils/error_parser";
import { Celebration } from "../../common/Celebration";
import {
  textColorNeutral700,
  textColorNeutral800,
  textColorNeutral900,
} from "../../common/style_vanilla.css";
import { mainContentWrapper } from "../../style_vanilla.css";
import { DocumentCollector } from "../DocumentCollector";
import { DocumentData } from "../DocumentCollector/types";
import { dateDisplay, inviteSummaryWrapper } from "./style_vanilla.css";
import { mapGuestDocumentsToFormProps } from "./utils";
import type { DocumentCollectorGuestProps } from "../DocumentCollectorGuest/types";

export const DocumentCollectorGuestv1 = ({
  documentUploadInviteDetails,
  token,
}: DocumentCollectorGuestProps) => {
  const [showCompleted, setShowCompleted] = useState(false);
  const { processDocuments, loading } = useGuestDocumentProcessor(token);
  const { skipDocumentUpload } = useGuestDocumentUpload(token);

  const documentDetails = useMemo(
    () =>
      mapGuestDocumentsToFormProps(
        documentUploadInviteDetails?.rulesEngine?.documentTypes || [],
      ),
    [documentUploadInviteDetails?.rulesEngine?.documentTypes],
  );

  const handleDocumentProcessing = useCallback(
    (newFiles: DocumentData[], deleteDocIds: number[]) => {
      if (!newFiles.length && !deleteDocIds.length) {
        skipDocumentUpload(token)
          .then(() => {
            setShowCompleted(true);
            toast.success("Updated successfully");
          })
          .catch((error) => {
            toast.error(getErrorMessage(error));
          });
      } else {
        processDocuments(newFiles, deleteDocIds)
          .then(() => {
            setShowCompleted(true);
            toast.success("Documents updated successfully");
          })
          .catch((error) => {
            toast.error(getErrorMessage(error));
          });
      }
    },
    [processDocuments, skipDocumentUpload, token],
  );

  useEffect(() => {
    if (documentDetails.length === 0) {
      setShowCompleted(true);
      return;
    }
    setShowCompleted(
      documentUploadInviteDetails?.status === DocumentUploadInviteStatus.UploadCompleted,
    );
  }, [documentUploadInviteDetails, documentDetails]);

  if (showCompleted) {
    return <Celebration />;
  }

  return (
    <Card
      className={mainContentWrapper}
      title={`Documents for ${documentUploadInviteDetails?.complianceProfile?.profileName}`}
    >
      <Text
        variant="regular-md"
        className={textColorNeutral800}
      >{`${documentUploadInviteDetails?.subscriberName} (${documentUploadInviteDetails?.subscriberEmailAddress}) has invited you to provide some information.`}</Text>
      <Flex className={inviteSummaryWrapper}>
        <Flex justifyContent="space-between" gap={Sizes.XS}>
          <Text variant="regular-md" lines={1} className={textColorNeutral900}>
            Message from
          </Text>
          <Text variant="strong-md" lines={1} className={textColorNeutral900}>
            {documentUploadInviteDetails?.subscriberName}
          </Text>
          <Text variant="regular-sm" className={dateDisplay}>
            {formatDate(documentUploadInviteDetails?.emailInviteTimestamp)}
          </Text>
        </Flex>
        <Text variant="regular-md" className={textColorNeutral700}>
          {documentUploadInviteDetails?.message}
        </Text>
      </Flex>
      <DocumentCollector
        isSubmitting={loading}
        documentDetails={documentDetails}
        allowSkipToUpload={true}
        onComplete={handleDocumentProcessing}
        buttonConfig={{
          label: "Submit",
          showNoDocumentsTT: true,
        }}
        inviterInfo={{
          name: documentUploadInviteDetails?.subscriberName || "",
          email: documentUploadInviteDetails?.subscriberEmailAddress || "",
        }}
      />
    </Card>
  );
};
